import { Suspense } from "react";
import { useFormContext } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import type { PublicStudioListVM } from "@shared-kernel/application/ports/public/public-repository";
import { Alert } from "@shared-kernel/primary/shared/alert";
import { Label } from "@shared-kernel/primary/shared/shadcn/ui/label";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import { PERSONAL_STUDIO_LABEL } from "@academy-context/write/domain/constants/shared";
import type { TeacherOnboardingFormInputs } from "../form-validation/full-form.schema";
import { STUDIO_OPTION } from "../form-validation/shared";
import { useStudioSelection } from "./studio/use-studio-selection";
import { StudioOptionCard } from "./studio/studio-option-card";
import { StudioTypeSelector } from "./studio/studio-type-selector";
import { StudioDetailsForm } from "./studio/studio-details-form";

interface StudioStepProps {
  publicStudios: PublicStudioListVM[];
}

export const StudioStep = ({ publicStudios }: StudioStepProps) => {
  const {
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<TeacherOnboardingFormInputs>();
  const { selectedStudioOption, handleStudioTypeChange } = useStudioSelection(setValue, watch, clearErrors);
  const studioOptionError = (errors.studio as { option?: FieldError } | undefined)?.option?.message;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Lieu principal des cours</h2>
        <Alert variant="info">
          <div className="text-sm leading-relaxed">
            Merci de définir le lieu principal de vos cours parmis l&apos;une des options suivantes :
            <br />
            <br />
            <ul className="list-disc pl-6">
              <li>&quot;Studio Partenaire&quot; pour les studios réglés directement par Opéra Off</li>
              <li>&quot;Mon studio perso&quot; si vous donnez cours dans votre local personnel (sans frais)</li>
              <li>&quot;Autre Studio&quot; pour les studios réglés directement par vous</li>
            </ul>
            <br />
            💡 Le studio choisi sera votre studio favori, vous pourrez ajouter d&apos;autres studios dans l&apos;application plus tard.
          </div>
        </Alert>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Choisissez votre option :</Label>
          {studioOptionError && <p className="text-sm font-medium text-destructive">{studioOptionError}</p>}
        </div>
        <div className="mx-auto grid w-full max-w-[450px] grid-cols-1 gap-4">
          <StudioOptionCard
            title="Studio Partenaire"
            description="Un studio de notre réseau partenaire"
            isSelected={selectedStudioOption === STUDIO_OPTION.PARTNER}
            paymentBadgeText="Réglé par Opéra Off"
            paymentBadgeVariant="success"
            additionalBadges={[{ text: `${publicStudios.length} studios disponibles`, variant: "outline" }]}
            onClick={() => handleStudioTypeChange(STUDIO_OPTION.PARTNER)}
            studioType={STUDIO_OPTION.PARTNER}
          >
            <StudioTypeSelector studios={publicStudios} />
          </StudioOptionCard>

          <StudioOptionCard
            title={PERSONAL_STUDIO_LABEL}
            description="Votre studio personnel (sans prise en charge par Opéra Off)"
            isSelected={selectedStudioOption === STUDIO_OPTION.PERSONAL}
            onClick={() => handleStudioTypeChange(STUDIO_OPTION.PERSONAL)}
            studioType={STUDIO_OPTION.PERSONAL}
          >
            <Suspense fallback={<Skeleton className="h-32" />}>
              <StudioDetailsForm nameFieldProps={{ disabled: true }} priceFieldProps={{ disabled: true }} />
            </Suspense>
          </StudioOptionCard>

          <StudioOptionCard
            title="Autre Studio"
            description="Un autre studio réglé par vous même (avec remboursement par Opéra off)"
            isSelected={selectedStudioOption === STUDIO_OPTION.OTHER}
            paymentBadgeText="Réglé par vous"
            paymentBadgeVariant="warning"
            onClick={() => handleStudioTypeChange(STUDIO_OPTION.OTHER)}
            studioType={STUDIO_OPTION.OTHER}
          >
            <Suspense fallback={<Skeleton className="h-32" />}>
              <StudioDetailsForm />
            </Suspense>
          </StudioOptionCard>
        </div>
      </div>
    </div>
  );
};
